import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { DownloadSimpleIcon } from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { ActivityAttachment } from "../types/reviewTypes";
import { isAttachmentDownloadable } from "../utils/attachments";
import AttachmentRow from "./AttachmentRow";

interface AttachmentsTabProps {
  attachments: ActivityAttachment[];
  isDownloadingAll: boolean;
  isAttachmentBusy: (attachmentId: string) => boolean;
  onDownload: (attachment: ActivityAttachment) => void;
  onOpen: (attachment: ActivityAttachment) => void;
  onDownloadAll: (attachments: ActivityAttachment[]) => void;
}

function AttachmentsTab({
  attachments,
  isDownloadingAll,
  isAttachmentBusy,
  onDownload,
  onOpen,
  onDownloadAll,
}: AttachmentsTabProps) {
  const downloadableAttachments = attachments.filter(isAttachmentDownloadable);
  const isDownloadAllDisabled = isDownloadingAll || downloadableAttachments.length === 0;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <CustomText
          text={`${attachments.length} file${attachments.length === 1 ? "" : "s"} to attach here`}
          useTranslationText={false}
          size="xs"
          style={styles.headerCount}
        />
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Download all attachments"
          accessibilityState={{ disabled: isDownloadAllDisabled }}
          disabled={isDownloadAllDisabled}
          onPress={() => onDownloadAll(downloadableAttachments)}
          style={[styles.downloadAllButton, isDownloadAllDisabled && styles.downloadAllButtonDisabled]}>
          <DownloadSimpleIcon size={14} color={colors.grey[700]} weight="regular" />
          <CustomText
            text={isDownloadingAll ? "Downloading" : "Download All"}
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={styles.downloadAllText}
          />
        </Pressable>
      </View>

      <View style={styles.list}>
        {attachments.map((attachment, index) => (
          <AttachmentRow
            key={attachment.id}
            attachment={attachment}
            isFirst={index === 0}
            isBusy={isAttachmentBusy(attachment.id)}
            onDownload={onDownload}
            onOpen={onOpen}
          />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerCount: {
    color: colors.grey[500],
  },
  downloadAllButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    height: 28,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  downloadAllButtonDisabled: {
    opacity: 0.5,
  },
  downloadAllText: {
    color: colors.grey[700],
  },
  list: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: colors.white,
  },
});

export default AttachmentsTab;
